import { useState } from "react";
import { Alert, Button, Empty, Segmented, Select } from "antd";

import Loader from "@/components/UI/Loader";
import {
  ecgPointsForSweepSpeed,
  isNotFoundError,
  useEcgWaveformQuery,
} from "@/hooks/useWaveformQueries";
import type { EcgLeadSamples, EcgWaveformResponse } from "../types";
import { PX_PER_MM } from "./constants";
import { EcgLeadStrip } from "./EcgLeadStrip";

const GAIN_OPTIONS_MM_PER_MV = [5, 10, 20];
const SPEED_OPTIONS_MM_PER_S = [12.5, 25, 50];
const DEFAULT_GAIN_MM_PER_MV = 10;
const DEFAULT_SPEED_MM_PER_S = 25;

/** Classic 3×4 print layout: 4 columns × 3 rows, 2.5 s per cell. */
const GRID_LEAD_COLUMNS = [
  ["I", "II", "III"],
  ["aVR", "aVL", "aVF"],
  ["V1", "V2", "V3"],
  ["V4", "V5", "V6"],
];
const GRID_SEGMENT_MS = 2500;
const GRID_CELL_HEIGHT_MM = 22;
const RHYTHM_LEAD = "II";
const GRID_ROW_COUNT = 3;

const EMPTY_DESCRIPTION = "No waveform available for this study";

type EcgLayout = "stacked" | "grid";

export interface EcgWaveformViewerProps {
  imagingStudyId: string;
}

const sliceSegment = (
  timesMs: number[],
  amplitudesUv: number[],
  startMs: number,
  endMs: number,
) => {
  let lo = 0;
  while (lo < timesMs.length && timesMs[lo] < startMs) lo += 1;
  let hi = lo;
  while (hi < timesMs.length && timesMs[hi] < endMs) hi += 1;
  return {
    timesMs: timesMs.slice(lo, hi),
    amplitudesUv: amplitudesUv.slice(lo, hi),
  };
};

interface LayoutProps {
  waveform: EcgWaveformResponse;
  mmPerSecond: number;
  mmPerMv: number;
}

interface StackedLayoutProps extends LayoutProps {
  visibleLeads: EcgLeadSamples[];
}

const StackedLayout = ({
  waveform,
  visibleLeads,
  mmPerSecond,
  mmPerMv,
}: StackedLayoutProps) => (
  <div className="overflow-x-auto">
    <div className="flex flex-col gap-2 w-max">
      {visibleLeads.map((lead) => (
        <EcgLeadStrip
          key={lead.lead_name}
          leadName={lead.lead_name}
          timesMs={waveform.times_ms}
          amplitudesUv={lead.amplitude_uv}
          pxPerMm={PX_PER_MM}
          mmPerSecond={mmPerSecond}
          mmPerMv={mmPerMv}
        />
      ))}
    </div>
  </div>
);

const GridLayout = ({ waveform, mmPerSecond, mmPerMv }: LayoutProps) => {
  const leadsByName = new Map(
    waveform.leads.map((lead) => [lead.lead_name, lead]),
  );
  const t0 = waveform.times_ms[0] ?? 0;
  const cellHeight = GRID_CELL_HEIGHT_MM * PX_PER_MM;
  const rhythmLead = leadsByName.get(RHYTHM_LEAD);

  return (
    <div className="overflow-x-auto">
      <div className="w-max">
        <div className="grid grid-cols-4 gap-x-2 gap-y-1">
          {Array.from({ length: GRID_ROW_COUNT }, (_, row) =>
            GRID_LEAD_COLUMNS.map((column, col) => {
              const leadName = column[row];
              const lead = leadsByName.get(leadName);
              if (!lead) {
                return (
                  <div
                    key={leadName}
                    className="flex items-center justify-center text-xs text-gray-400"
                    style={{ height: cellHeight }}
                  >
                    {leadName} — no data
                  </div>
                );
              }
              const segment = sliceSegment(
                waveform.times_ms,
                lead.amplitude_uv,
                t0 + col * GRID_SEGMENT_MS,
                t0 + (col + 1) * GRID_SEGMENT_MS,
              );
              return (
                <EcgLeadStrip
                  key={leadName}
                  leadName={leadName}
                  timesMs={segment.timesMs}
                  amplitudesUv={segment.amplitudesUv}
                  pxPerMm={PX_PER_MM}
                  mmPerSecond={mmPerSecond}
                  mmPerMv={mmPerMv}
                  height={cellHeight}
                />
              );
            }),
          )}
        </div>
        {rhythmLead && (
          <div className="mt-2">
            <EcgLeadStrip
              leadName={`${RHYTHM_LEAD} (rhythm)`}
              timesMs={waveform.times_ms}
              amplitudesUv={rhythmLead.amplitude_uv}
              pxPerMm={PX_PER_MM}
              mmPerSecond={mmPerSecond}
              mmPerMv={mmPerMv}
              height={cellHeight}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const EcgWaveformViewer = ({
  imagingStudyId,
}: EcgWaveformViewerProps) => {
  const [layout, setLayout] = useState<EcgLayout>("stacked");
  const [mmPerMv, setMmPerMv] = useState(DEFAULT_GAIN_MM_PER_MV);
  const [mmPerSecond, setMmPerSecond] = useState(DEFAULT_SPEED_MM_PER_S);
  // null = all leads selected (the default, applied once data arrives).
  const [selectedLeads, setSelectedLeads] = useState<string[] | null>(null);

  // Only as many points as the strip can draw at the chosen sweep speed.
  const { data, isLoading, error, refetch } = useEcgWaveformQuery({
    imagingStudyId,
    maxPoints: ecgPointsForSweepSpeed(mmPerSecond),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader noMargin />
      </div>
    );
  }

  if (error && isNotFoundError(error)) {
    return <Empty description={EMPTY_DESCRIPTION} />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Waveform service unavailable"
        description="The ECG waveform could not be loaded. The time-series database may be temporarily unreachable."
        action={
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (!data || data.leads.length === 0) {
    return <Empty description={EMPTY_DESCRIPTION} />;
  }

  const allLeadNames = data.leads.map((lead) => lead.lead_name);
  const activeLeadNames = selectedLeads ?? allLeadNames;
  const visibleLeads = data.leads.filter((lead) =>
    activeLeadNames.includes(lead.lead_name),
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Segmented
          value={layout}
          onChange={(value) => setLayout(value as EcgLayout)}
          options={[
            { label: "Stacked", value: "stacked" },
            { label: "3×4 Grid", value: "grid" },
          ]}
        />
        <Select
          size="middle"
          value={mmPerMv}
          onChange={setMmPerMv}
          options={GAIN_OPTIONS_MM_PER_MV.map((gain) => ({
            value: gain,
            label: `${gain} mm/mV`,
          }))}
          popupMatchSelectWidth={false}
        />
        <Select
          size="middle"
          value={mmPerSecond}
          onChange={setMmPerSecond}
          options={SPEED_OPTIONS_MM_PER_S.map((speed) => ({
            value: speed,
            label: `${speed} mm/s`,
          }))}
          popupMatchSelectWidth={false}
        />
        <Select
          mode="multiple"
          className="min-w-[220px] flex-1 max-w-[420px]"
          value={activeLeadNames}
          onChange={(values: string[]) => setSelectedLeads(values)}
          options={allLeadNames.map((name) => ({ value: name, label: name }))}
          maxTagCount="responsive"
          disabled={layout === "grid"}
          placeholder="Leads"
        />
      </div>
      {layout === "stacked" ? (
        <StackedLayout
          waveform={data}
          visibleLeads={visibleLeads}
          mmPerSecond={mmPerSecond}
          mmPerMv={mmPerMv}
        />
      ) : (
        <GridLayout
          waveform={data}
          mmPerSecond={mmPerSecond}
          mmPerMv={mmPerMv}
        />
      )}
    </div>
  );
};
